import React from "react";
import { ShieldCheck, ChevronRight } from "lucide-react";

/**
 * VaultRankLadder — visualises the user's progression through all 6 vault
 * ranks. Each rank's color is from the backend's VAULT_RANKS table.
 *
 * Renders as a stacked column of mini-tiles; the user's CURRENT rank gets
 * a filled background + glow, future ranks are dimmed, past ranks have a
 * subtle "✓ UNLOCKED" marker.
 *
 * Props:
 *   currentTitle  — e.g. "OPERATIVE"  (string from public_profile.vault_rank.title)
 *   sigilCount    — current sigil count to derive past/future status
 */
const VAULT_RANKS = [
  { threshold: 0, title: "INITIATE", color: "#94a3b8", tagline: "the file just opened" },
  { threshold: 3, title: "OPERATIVE", color: "#60a5fa", tagline: "tracking the pattern" },
  { threshold: 7, title: "CELL LEAD", color: "#a78bfa", tagline: "you brought the others" },
  { threshold: 12, title: "ARCHIVIST", color: "#00ff88", tagline: "the room is yours now" },
  { threshold: 18, title: "DOSSIER MAGE", color: "#ffd60a", tagline: "you decide what's real" },
  { threshold: 25, title: "VAULT CUSTODIAN", color: "#ff3b30", tagline: "the seal is in your hand" },
];

export default function VaultRankLadder({ currentTitle, sigilCount = 0 }) {
  return (
    <ol className="space-y-2" data-testid="vault-rank-ladder">
      {VAULT_RANKS.map((r, idx) => {
        const unlocked = sigilCount >= r.threshold;
        const isCurrent = currentTitle === r.title;
        const isLast = idx === VAULT_RANKS.length - 1;
        // Show remaining count for the next-up unlock so users see momentum.
        const distance = r.threshold - sigilCount;
        const isNext = !isCurrent && !unlocked && (idx === 0 || VAULT_RANKS[idx - 1].threshold <= sigilCount);
        return (
          <li
            key={r.title}
            data-testid={`rank-tier-${r.title.toLowerCase().replace(/\s+/g, "-")}`}
            className="relative flex items-center gap-3 px-3 py-2.5 border"
            style={{
              borderColor: isCurrent ? r.color : unlocked ? `${r.color}55` : "rgba(255,255,255,0.07)",
              background: isCurrent
                ? `linear-gradient(90deg, ${r.color}1f 0%, transparent 100%)`
                : "transparent",
              boxShadow: isCurrent ? `0 0 0 1px ${r.color}66, 0 12px 32px -18px ${r.color}88` : "none",
              opacity: unlocked || isCurrent ? 1 : 0.55,
            }}
          >
            <ShieldCheck
              size={14}
              style={{ color: isCurrent || unlocked ? r.color : "#3f3f46" }}
              aria-hidden
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="font-heading font-black uppercase tracking-tight text-sm sm:text-base leading-none"
                  style={{ color: isCurrent ? "#fff" : unlocked ? r.color : "#71717a" }}
                >
                  {r.title}
                </span>
                {isCurrent && (
                  <span
                    className="px-1.5 py-0.5 font-mono text-[8px] tracking-[0.25em] uppercase"
                    style={{ backgroundColor: r.color, color: "#050505" }}
                  >
                    YOU
                  </span>
                )}
                {!isCurrent && unlocked && (
                  <span className="font-mono text-[8px] tracking-[0.25em] uppercase text-zinc-500">
                    UNLOCKED
                  </span>
                )}
                {isNext && (
                  <span className="font-mono text-[8px] tracking-[0.25em] uppercase text-zinc-500">
                    {distance} SIGIL{distance === 1 ? "" : "S"} AWAY
                  </span>
                )}
              </div>
              <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500 italic mt-0.5 truncate">
                {r.tagline}
              </div>
            </div>
            {!isLast && (
              <ChevronRight size={12} className="shrink-0 opacity-30" aria-hidden />
            )}
          </li>
        );
      })}
    </ol>
  );
}
